/**
 * In-memory buffer of error-level logs.
 *
 * Captures every `console.error` (and, because `Logger.error` routes through
 * `console.error`, every `Logger.error`) into a bounded ring buffer so the
 * dev-only "copy error logs" affordance on the Roadmap can export them as
 * formatted JSON for later debugging.
 *
 * This is a debugging aid: the buffer lives in memory only (never persisted),
 * is capped at {@link MAX_ENTRIES}, and the original `console.error` is always
 * still called so normal logging/RUM behavior is unchanged.
 */

export interface ErrorLogEntry {
  /** ISO-8601 timestamp of when the error was logged. */
  timestamp: string;
  level: 'error';
  /** The serialized `console.error` arguments, one per call argument. */
  messages: unknown[];
}

/** Cap on retained entries; oldest are dropped once exceeded. */
const MAX_ENTRIES = 500;

const entries: ErrorLogEntry[] = [];

let originalConsoleError: ((...args: unknown[]) => void) | null = null;
let installed = false;

/**
 * Convert a single `console.error` argument into a JSON-serializable value.
 * Errors keep their name/message/stack; circular refs, functions and bigints
 * are stringified so the buffer never throws when later serialized.
 */
function serializeArg(value: unknown): unknown {
  const seen = new WeakSet<object>();
  const replacer = (_key: string, val: unknown): unknown => {
    if (val instanceof Error) {
      return { name: val.name, message: val.message, stack: val.stack };
    }
    if (typeof val === 'function') {
      return `[Function ${(val as { name?: string }).name || 'anonymous'}]`;
    }
    if (typeof val === 'bigint') {
      return val.toString();
    }
    if (typeof val === 'object' && val !== null) {
      if (seen.has(val)) return '[Circular]';
      seen.add(val);
    }
    return val;
  };

  try {
    return JSON.parse(JSON.stringify(value, replacer));
  } catch {
    return String(value);
  }
}

/** Record an error log entry, trimming the buffer to {@link MAX_ENTRIES}. */
function recordError(args: unknown[]): void {
  entries.push({
    timestamp: new Date().toISOString(),
    level: 'error',
    messages: args.map(serializeArg),
  });
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES);
  }
}

/**
 * Patch `console.error` so every call is recorded before delegating to the
 * original implementation. Idempotent — safe to call more than once.
 */
export function installErrorCapture(): void {
  if (installed) return;
  installed = true;
  originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]): void => {
    recordError(args);
    originalConsoleError?.(...args);
  };
}

/** Number of error entries currently buffered. */
export function getErrorLogCount(): number {
  return entries.length;
}

/** Pretty-printed JSON snapshot of all buffered error logs. */
export function getFormattedErrorLogs(): string {
  return JSON.stringify(
    {
      exportedAt: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      count: entries.length,
      errors: entries,
    },
    null,
    2,
  );
}

/** Clear the buffered error logs. */
export function clearErrorLogs(): void {
  entries.length = 0;
}
